// Client-side dominant color extractor for Playbook cover images.
// Draws the image onto a tiny 48×48 canvas, buckets every non-transparent
// pixel into a 24-step color cube, and returns the average color of the most
// popular non-neutral bucket. Falls back to `null` if the image can't be read
// (e.g. CORS-blocked external host, decode failure).
//
// Neutral (very light / very dark / very grey) buckets are down-weighted so
// the poster mood color wins over the white / black background around it.
export async function extractDominantColor(imageSrc, { fallback = null } = {}) {
  if (!imageSrc) return fallback;
  try {
    const img = await loadImage(imageSrc);
    const size = 48;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return fallback;
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    const step = 32; // 8 buckets per channel
    const buckets = new Map();
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 200) continue; // ignore transparent edges
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const key =
        (Math.floor(r / step)) * 100 +
        (Math.floor(g / step)) * 10 +
        (Math.floor(b / step));
      let entry = buckets.get(key);
      if (!entry) {
        entry = { r: 0, g: 0, b: 0, n: 0 };
        buckets.set(key, entry);
      }
      entry.r += r;
      entry.g += g;
      entry.b += b;
      entry.n += 1;
    }

    let best = null;
    for (const b of buckets.values()) {
      const r = b.r / b.n;
      const g = b.g / b.n;
      const bl = b.b / b.n;
      // Down-weight near-white, near-black, and near-grey so the mood color
      // (the actual illustration color) beats the paper/void surrounding it.
      const maxC = Math.max(r, g, bl);
      const minC = Math.min(r, g, bl);
      const saturation = maxC === 0 ? 0 : (maxC - minC) / maxC;
      const brightness = (r + g + bl) / 3;
      const isNeutral = saturation < 0.15 || brightness < 20 || brightness > 235;
      const weight = b.n * (isNeutral ? 0.15 : 1);
      if (!best || weight > best.weight) {
        best = { r, g, b: bl, weight };
      }
    }
    if (!best) return fallback;
    return rgbToHex(best.r, best.g, best.b);
  } catch {
    return fallback;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Data URLs don't need CORS; external URLs need the remote host to allow
    // us — set anonymous and let it fail silently if not permitted.
    if (!/^data:/.test(src)) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function rgbToHex(r, g, b) {
  const hx = (n) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0");
  return `#${hx(r)}${hx(g)}${hx(b)}`;
}
